// Activation predicate evaluator. ADR 0014 §2.1 / §2.10 / §2.11.
//
// Skill firing path で Skill.activation を統合的に評価し、9 フィールド
// (`jobTypes` / `statsAtLeast` / `selfHpAtMost` / `targetHpAtMost` /
// `requiresDistance` / `requiresPositionRelative` / `requiresEnemyCountInRadius`
// / `requiresBehindTarget` / `targetHasStatusAtLeast`) + `requiresBehaviorRule*` 3
// + `jobTypeBonus` を AND 評価し、発火可否と (発火可なら) `fireChanceMod` /
// `effectAmountMod` を返す。
//
// 呼び出し点: `packages/sim/src/battle/tick.ts` Step 4 (Combat resolution)
// および `packages/sim/src/battle/reactive-events.ts` の各 event 発火点。
//
// `candidates.ts` は呼び出さない (ADR 0014 §3 不採用案、AI scoring 段階での
// Activation gate は本 PR スコープ外)。
const EPSILON = 1e-6;
const PASS = Object.freeze({
    canFire: true,
    fireChanceMod: 0,
    effectAmountMod: 1,
});
const FAIL = Object.freeze({
    canFire: false,
    fireChanceMod: 0,
    effectAmountMod: 1,
});
/**
 * Resolve `enemyCountAtLeast` to `requiresEnemyCountInRadius` (canonical form).
 * ADR 0014 §2.10:
 *   - 両方設定 → `requiresEnemyCountInRadius` 優先、`enemyCountAtLeast` 無視
 *   - `enemyCountAtLeast` 単独 → `{ radius, minCount: count }` に内部変換
 *   - `requiresEnemyCountInRadius` 単独 → そのまま
 *   - どちらも未設定 → undefined
 */
function canonicalEnemyCount(activation) {
    if (activation.requiresEnemyCountInRadius !== undefined) {
        return activation.requiresEnemyCountInRadius;
    }
    if (activation.enemyCountAtLeast !== undefined) {
        return {
            radius: activation.enemyCountAtLeast.radius,
            minCount: activation.enemyCountAtLeast.count,
        };
    }
    return undefined;
}
function distance(a, b) {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    return Math.sqrt(dx * dx + dy * dy);
}
/**
 * Extract the ruleId from a BehaviorRule, handling the `damageReact`-only nesting
 * (`rule.ruleId` instead of `ruleId`). Other categories carry `ruleId` directly.
 */
function ruleIdOf(rule) {
    if (rule.category === 'damageReact')
        return rule.rule.ruleId;
    return rule.ruleId;
}
function stackCount(entity, statusId) {
    const inBuffs = entity.buffs.find((b) => b.id === statusId);
    if (inBuffs !== undefined)
        return inBuffs.stacks;
    const inDebuffs = entity.debuffs.find((b) => b.id === statusId);
    if (inDebuffs !== undefined)
        return inDebuffs.stacks;
    return 0;
}
/**
 * Compute the caster's effective facing vector. ADR 0014 §2.11:
 *   1. `caster.facing` 設定済 → そのまま
 *   2. 未設定 → `caster.velocity` を facing として扱う
 *   3. `velocity` ≒ 0 (停止中) → null (背面判定は不発)
 */
function effectiveFacing(caster) {
    const f = caster.facing;
    if (f !== undefined) {
        const len = Math.hypot(f.x, f.y);
        if (len > EPSILON)
            return { x: f.x / len, y: f.y / len };
    }
    const v = caster.velocity;
    const vlen = Math.hypot(v.x, v.y);
    if (vlen <= EPSILON)
        return null;
    return { x: v.x / vlen, y: v.y / vlen };
}
/**
 * `caster` が `target` の背面 ±halfAngleDegrees に居るかを判定する。
 * 背面ベクトルは「target の facing の逆方向」だが、target.facing が無いケースも
 * あるため target.velocity を facing として扱い、停止中 target は「常に背面成立」
 * は危険なので **target も velocity ≒ 0 なら false** (caster と同じ規約)。
 */
function isBehindTarget(caster, target, halfAngleDegrees) {
    const targetFacing = effectiveFacing(target);
    if (targetFacing === null)
        return false;
    // 背面ベクトル (target が向いている方向の逆)
    const back = { x: -targetFacing.x, y: -targetFacing.y };
    // caster の target からの相対位置 (target 中心)
    const dx = caster.pos.x - target.pos.x;
    const dy = caster.pos.y - target.pos.y;
    const len = Math.hypot(dx, dy);
    if (len <= EPSILON)
        return false;
    const nx = dx / len;
    const ny = dy / len;
    // back との内積 = cos(θ)、θ ≤ halfAngleDegrees で背面成立
    const cos = back.x * nx + back.y * ny;
    const cosThreshold = Math.cos((halfAngleDegrees * Math.PI) / 180);
    return cos >= cosThreshold;
}
/**
 * Evaluate `Skill.activation` against the current state. Returns `canFire: false`
 * if any required predicate fails; otherwise `canFire: true` + jobTypeBonus mods.
 *
 * This function is **read-only**: it does not mutate `state`, `caster`, or `target`.
 *
 * Note on `Skill.activation.onEvent`: Activation 評価は「event 発火直前に呼ばれた時点で
 * 既に該当 event が発火している」前提で動く (reactive-events.ts 側で event filter)。
 * よって本関数は `onEvent` を判定対象に含めない (含めると逆に AI が active Skill を
 * 打てなくなる)。
 */
export function evaluateActivation(skill, caster, target, state) {
    const a = skill.activation;
    // 1. jobTypes (一致必須)
    if (a.jobTypes !== undefined && a.jobTypes.length > 0) {
        const match = a.jobTypes.some((jt) => caster.jobTypes.has(jt));
        if (!match)
            return FAIL;
    }
    // 2. statsAtLeast (stat 下限)
    if (a.statsAtLeast !== undefined) {
        for (const [key, value] of Object.entries(a.statsAtLeast)) {
            if (value === undefined)
                continue;
            const stat = caster.stats[key];
            if (stat === undefined || stat < value)
                return FAIL;
        }
    }
    // 3. selfHpAtMost (caster HP fraction 上限)
    if (a.selfHpAtMost !== undefined) {
        const frac = caster.hp / Math.max(caster.hpMax, EPSILON);
        if (frac > a.selfHpAtMost)
            return FAIL;
    }
    // 4. targetHpAtMost (target HP fraction 上限)
    if (a.targetHpAtMost !== undefined) {
        if (target === null)
            return FAIL;
        const frac = target.hp / Math.max(target.hpMax, EPSILON);
        if (frac > a.targetHpAtMost)
            return FAIL;
    }
    // 5. requiresDistance (軸 8 archetype 1/8)
    if (a.requiresDistance !== undefined) {
        if (target === null)
            return FAIL;
        const d = distance(caster.pos, target.pos);
        if (a.requiresDistance.min !== undefined && d < a.requiresDistance.min)
            return FAIL;
        if (a.requiresDistance.max !== undefined && d > a.requiresDistance.max)
            return FAIL;
    }
    // 6. requiresPositionRelative (軸 8 archetype 4/5)
    if (a.requiresPositionRelative !== undefined) {
        const { reference, maxDistance } = a.requiresPositionRelative;
        let anchors = [];
        switch (reference) {
            case 'caster':
                anchors = [caster.pos];
                break;
            case 'placeable':
                anchors = state.placeables
                    .filter((p) => p.ownerId === caster.id)
                    .map((p) => p.position);
                break;
            case 'summon':
                anchors = state.summons
                    .map((s) => state.entities.find((e) => e.id === s.entityId))
                    .filter((e) => e !== undefined && e.hp > 0)
                    .filter((e) => {
                    const inst = state.summons.find((s) => s.entityId === e.id);
                    return inst !== undefined && inst.ownerId === caster.id;
                })
                    .map((e) => e.pos);
                break;
        }
        if (anchors.length === 0)
            return FAIL;
        // caster (or target) が anchor の maxDistance 内に居るか。
        // 用途は「友軍オブジェクト周辺」なので caster 基準で判定。
        const within = anchors.some((p) => distance(caster.pos, p) <= maxDistance);
        if (!within)
            return FAIL;
    }
    // 7. requiresEnemyCountInRadius (軸 8 archetype 3) + enemyCountAtLeast canonical 化
    const enemyCount = canonicalEnemyCount(a);
    if (enemyCount !== undefined) {
        let count = 0;
        for (const e of state.entities) {
            if (e.hp <= 0)
                continue;
            if (e.side === caster.side)
                continue;
            if (distance(caster.pos, e.pos) <= enemyCount.radius)
                count += 1;
        }
        if (enemyCount.minCount !== undefined && count < enemyCount.minCount)
            return FAIL;
        if (enemyCount.maxCount !== undefined && count > enemyCount.maxCount)
            return FAIL;
    }
    // 8. requiresBehindTarget (軸 8 archetype 2)
    if (a.requiresBehindTarget !== undefined) {
        if (target === null)
            return FAIL;
        if (!isBehindTarget(caster, target, a.requiresBehindTarget.halfAngleDegrees))
            return FAIL;
    }
    // 9. targetHasStatusAtLeast (軸 1 / 軸 2)
    if (a.targetHasStatusAtLeast !== undefined) {
        if (target === null)
            return FAIL;
        const stacks = stackCount(target, a.targetHasStatusAtLeast.statusId);
        if (stacks < a.targetHasStatusAtLeast.stacks)
            return FAIL;
    }
    // 10-12. requiresBehaviorRule* (軸 5、archetype 1-6 / 7 / 8)
    if (a.requiresBehaviorRule !== undefined) {
        const { category, ruleId } = a.requiresBehaviorRule;
        const matched = caster.behaviorRules.some((r) => r.category === category && ruleIdOf(r) === ruleId);
        if (!matched)
            return FAIL;
    }
    if (a.requiresBehaviorRuleCount !== undefined) {
        // 6 core カテゴリ + リング 2 = 最大 8 スロット、`trigger`/`startBuff` は除外 (型レベル)。
        // 空 ruleId は除外。caster.behaviorRules の長さでカウント (sim 側責務、空配列前提)。
        const count = caster.behaviorRules.filter((r) => r.category !== 'trigger' && r.category !== 'startBuff').length;
        if (a.requiresBehaviorRuleCount.min !== undefined &&
            count < a.requiresBehaviorRuleCount.min) {
            return FAIL;
        }
        if (a.requiresBehaviorRuleCount.max !== undefined &&
            count > a.requiresBehaviorRuleCount.max) {
            return FAIL;
        }
    }
    if (a.requiresBehaviorRuleCombo !== undefined) {
        for (const required of a.requiresBehaviorRuleCombo) {
            const matched = caster.behaviorRules.some((r) => r.category === required.category && ruleIdOf(r) === required.ruleId);
            if (!matched)
                return FAIL;
        }
    }
    // 13. jobTypeBonus (発火率 / 効果量加算、AND ゲートではない)
    if (a.jobTypeBonus !== undefined && caster.jobTypes.has(a.jobTypeBonus.jobType)) {
        return {
            canFire: true,
            fireChanceMod: a.jobTypeBonus.fireChanceMod,
            effectAmountMod: a.jobTypeBonus.effectAmountMod,
        };
    }
    return PASS;
}
//# sourceMappingURL=activation.js.map